import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LayoutGrid, Plus } from 'lucide-react';
import { PageHeader } from '@/components/shared/page';
import { DataTable, type Column } from '@/components/shared/data-table';
import { Can } from '@/components/shared/can';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select } from '@/components/ui/misc';
import { useDebounce } from '@/hooks/use-debounce';
import { usePagination } from '@/hooks/use-pagination';
import { formatDate, formatMoney } from '@/lib/format';
import { useDeals, usePipelines } from './api';
import { DEAL_TYPE_LABELS } from './labels';
import { DealDialog } from './deal-dialog';
import type { Deal, DealStatus } from './types';

const STATUS_VARIANT: Record<DealStatus, 'default' | 'success' | 'destructive'> = {
  OPEN: 'default',
  WON: 'success',
  LOST: 'destructive',
};

export function DealsListPage() {
  const pagination = usePagination();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [pipelineId, setPipelineId] = useState('');
  const [sort, setSort] = useState('');
  const [creating, setCreating] = useState(false);

  const debounced = useDebounce(search);
  const { data: pipelines = [] } = usePipelines();
  const { data, isLoading, error, refetch } = useDeals({
    page: pagination.page,
    limit: pagination.limit,
    q: debounced || undefined,
    status: status || undefined,
    pipelineId: pipelineId || undefined,
    sort: sort || undefined,
  });

  const columns: Column<Deal>[] = [
    {
      key: 'title',
      header: 'Deal',
      sortKey: 'title',
      cell: (row) => (
        <div className="min-w-0">
          <div className="truncate font-medium">{row.title}</div>
          <div className="truncate text-xs text-muted-foreground">
            {row.company.name} · {row.number}
          </div>
        </div>
      ),
    },
    {
      key: 'stage',
      header: 'Stage',
      cell: (row) => (
        <span className="flex items-center gap-1.5 text-sm">
          <span
            className="h-2 w-2 shrink-0 rounded-full"
            style={{ backgroundColor: row.stage.colour }}
          />
          {row.stage.name}
        </span>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      className: 'text-sm text-muted-foreground',
      cell: (row) => DEAL_TYPE_LABELS[row.dealType],
    },
    {
      key: 'value',
      header: 'Value',
      sortKey: 'value',
      className: 'tabular text-sm font-medium',
      cell: (row) => formatMoney(row.value, row.currency),
    },
    {
      key: 'weighted',
      header: 'Weighted',
      className: 'tabular text-sm text-muted-foreground',
      cell: (row) => formatMoney(row.weightedValue, row.currency),
    },
    {
      key: 'close',
      header: 'Expected close',
      sortKey: 'expected_close_date',
      className: 'whitespace-nowrap text-sm text-muted-foreground',
      cell: (row) => formatDate(row.expectedCloseDate),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (row) => (
        <div className="flex flex-col gap-0.5">
          <Badge variant={STATUS_VARIANT[row.status]}>{row.status}</Badge>
          {row.status === 'LOST' && row.lostReason && (
            <span className="text-[10px] text-muted-foreground">{row.lostReason}</span>
          )}
        </div>
      ),
    },
    {
      key: 'owner',
      header: 'Owner',
      className: 'text-sm text-muted-foreground',
      cell: (row) => row.owner?.name ?? 'Unassigned',
    },
  ];

  return (
    <div>
      <PageHeader
        title="Deals"
        description="Every opportunity, including the ones already won and lost."
        actions={
          <div className="flex items-center gap-2">
            <Button asChild variant="outline" size="sm">
              <Link to="/crm/deals">
                <LayoutGrid className="h-4 w-4" />
                Board
              </Link>
            </Button>
            <Can permission="crm.deal.create">
              <Button onClick={() => setCreating(true)}>
                <Plus className="h-4 w-4" />
                New deal
              </Button>
            </Can>
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Input
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            pagination.reset();
          }}
          placeholder="Search title, number or company…"
          className="h-9 w-full sm:max-w-xs"
        />
        <Select
          value={status}
          onChange={(event) => {
            setStatus(event.target.value);
            pagination.reset();
          }}
          className="h-9 w-auto min-w-[8rem]"
        >
          <option value="">All statuses</option>
          <option value="OPEN">Open</option>
          <option value="WON">Won</option>
          <option value="LOST">Lost</option>
        </Select>
        <Select
          value={pipelineId}
          onChange={(event) => {
            setPipelineId(event.target.value);
            pagination.reset();
          }}
          className="h-9 w-auto min-w-[11rem]"
        >
          <option value="">All pipelines</option>
          {pipelines.map((pipeline) => (
            <option key={pipeline.id} value={pipeline.id}>
              {pipeline.name}
            </option>
          ))}
        </Select>
      </div>

      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        rowKey={(row) => row.id}
        isLoading={isLoading}
        error={error ? 'Could not load deals.' : null}
        onRetry={() => void refetch()}
        meta={data?.meta}
        onPageChange={pagination.setPage}
        onRowClick={(row) => navigate(`/crm/deals/${row.id}`)}
        sort={sort}
        onSortChange={setSort}
        emptyTitle="No deals yet"
        emptyDescription="Create one, or convert a lead to start a pipeline."
      />

      {creating && <DealDialog onClose={() => setCreating(false)} />}
    </div>
  );
}
